import { Box, Page, Table } from '@wix/design-system';
import React, { FunctionComponent, ReactElement, ReactNode } from 'react';
import { ToolbarCollectionState } from '../../state';
import { observer } from 'mobx-react-lite';
import {
  CollectionToolbar,
  CollectionToolbarProps,
} from '../CollectionToolbar';
import { FiltersMap } from '@wix/bex-core';
import { CollectionSearchProps } from '../CollectionSearch';
import { PartialFunctionComponent } from '../PartialFunctionComponent';
import { FilterElement } from '../CollectionToolbarFilters';
import { CollectionToolbarsProvider } from './CollectionToolbarsProvider';
import { ToolbarAndTitlebarContainer } from './ToolbarAndTitlebarContainer';
import type { TagsElement } from '../Tags';
import { ToolbarCollectionBaseProps } from '../ToolbarCollection';

export interface CollectionToolbarsProps<T, F extends FiltersMap> {
  state: ToolbarCollectionState<T, F>;
  title?: ToolbarCollectionBaseProps<T, F>['title'];
  subtitle?: ToolbarCollectionBaseProps<T, F>['subtitle'];
  tabs?: ToolbarCollectionBaseProps<T, F>['tabs'];
  search?: boolean | FilterElement;
  customColumns?: CollectionToolbarProps<T, F>['customColumns'] | null;
  multiLevelSorting?: CollectionToolbarProps<T, F>['multiLevelSorting'];
  views?: CollectionToolbarProps<T, F>['views'];
  onSearchChange?: CollectionSearchProps<T, F>['onSearchChange'];
  onSearchClick?: CollectionSearchProps<T, F>['onSearchClick'];
  onSearchBlur?: CollectionSearchProps<T, F>['onSearchBlur'];
  searchMaxLength?: CollectionSearchProps<T, F>['maxLength'];
  sticky: boolean;
  filters?: CollectionToolbarProps<T, F>['filters'];
  showTotal?: ToolbarCollectionBaseProps<T, F>['showTotal'];
  selectionToolbar?: ReactElement;
  dragAndDropReorderModeToolbar?:
    | CollectionToolbarProps<T, F>['dragAndDropReorderModeToolbar']
    | null;
  extraToolbar?: ToolbarCollectionBaseProps<T, F>['extraToolbar'];
  summaryBar?: ToolbarCollectionBaseProps<T, F>['summaryBar'];
  titleBar?: ReactNode;
  tableGridSwitchButton?: CollectionToolbarProps<T, F>['tableGridSwitchButton'];
  titleBarDisplay?: boolean;
  exportButton?: ReactElement;
  tags?: TagsElement<T>;
  addNewButton?: CollectionToolbarProps<T, F>['addNewButton'];
  primaryActionButton?: CollectionToolbarProps<T, F>['primaryActionButton'];
}

function BorderRadiusTopCard({ children }: { children?: ReactNode }) {
  return (
    <Box
      direction="vertical"
      backgroundColor="D80"
      borderTopLeftRadius={8}
      borderTopRightRadius={8}
    >
      {children}
    </Box>
  );
}

function _CollectionToolbars<T, F extends FiltersMap = FiltersMap>(
  props: CollectionToolbarsProps<T, F>,
) {
  const {
    sticky,
    state,
    search,
    onSearchChange,
    onSearchClick,
    onSearchBlur,
    searchMaxLength,
    selectionToolbar,
    dragAndDropReorderModeToolbar,
    filters,
    showTotal,
    title,
    subtitle,
    tabs,
    customColumns,
    multiLevelSorting,
    views,
    extraToolbar,
    summaryBar,
    titleBar,
    tableGridSwitchButton,
    exportButton,
    addNewButton,
    primaryActionButton,
  } = props;

  const {
    collection,
    featuredComponents,
    hasSubToolbarComponents,
    shouldDisplayFiltersTags,
    bulk: { selectedIds },
    internalScroll,
  } = state;

  const AppliedFiltersTagList = featuredComponents.appliedFiltersTagList.value
    ?.component as PartialFunctionComponent as FunctionComponent | null;

  const SubToolbarExtension = featuredComponents.subToolbarExtension
    .value as PartialFunctionComponent as FunctionComponent | null;

  const { showRefreshLoader } = collection;

  const shouldDisplaySubToolbar =
    shouldDisplayFiltersTags && hasSubToolbarComponents;

  const toolbars = (
    <>
      {selectionToolbar && !showRefreshLoader && selectedIds.length > 0 ? (
        selectionToolbar
      ) : (
        <>
          {extraToolbar}
          <CollectionToolbar
            dataHook="collection-toolbar"
            search={search}
            onSearchChange={onSearchChange}
            onSearchClick={onSearchClick}
            onSearchBlur={onSearchBlur}
            searchMaxLength={searchMaxLength}
            customColumns={customColumns}
            multiLevelSorting={multiLevelSorting}
            views={views}
            state={state}
            filters={filters}
            showTotal={showTotal}
            title={title}
            subtitle={subtitle}
            tabs={tabs}
            tableGridSwitchButton={tableGridSwitchButton}
            exportButton={exportButton}
            addNewButton={addNewButton}
            primaryActionButton={primaryActionButton}
            dragAndDropReorderModeToolbar={dragAndDropReorderModeToolbar}
          />
        </>
      )}
      {shouldDisplaySubToolbar && (
        <Box direction="vertical" backgroundColor="D80">
          <Table.SubToolbar dataHook="collection-table-sub-toolbar">
            <Box direction="horizontal">
              <Box flex={1}>
                {AppliedFiltersTagList && shouldDisplayFiltersTags && (
                  <AppliedFiltersTagList />
                )}
              </Box>
              {SubToolbarExtension && <SubToolbarExtension />}
            </Box>
          </Table.SubToolbar>
        </Box>
      )}
      {summaryBar}
    </>
  );

  /* wrapping with Card is WSR way to make the sticky table header not to add extra border between the header and the content https://www.docs.wixdesignsystem.com/?path=/story/components-lists-table--table#Sticky_Toolbar_&_Sub-toolbar*/

  const child = (
    <ToolbarAndTitlebarContainer state={state}>
      <BorderRadiusTopCard>
        {toolbars}
        {titleBar}
      </BorderRadiusTopCard>
    </ToolbarAndTitlebarContainer>
  );

  return (
    <CollectionToolbarsProvider>
      {sticky && !internalScroll ? <Page.Sticky>{child}</Page.Sticky> : child}
    </CollectionToolbarsProvider>
  );
}

export const CollectionToolbars = observer(_CollectionToolbars);
